import { create } from 'zustand';
import {
  acceptOfferte,
  bumpOfferteRouteCapacity,
  fetchOfferteRoutes,
  type OfferteDetail,
  type OfferteRouteOption,
} from '../lib/offertes';
import type { PackageSku } from '../lib/offerte-constants';

export interface AddressFields {
  street: string;
  houseNumber: string;
  houseNumberAddition: string;
  postalCode: string;
  city: string;
}

const EMPTY_ADDRESS: AddressFields = {
  street: '',
  houseNumber: '',
  houseNumberAddition: '',
  postalCode: '',
  city: '',
};

type SubmitResult =
  | null
  | {
      ok: boolean;
      message?: string;
      workOrderId?: string;
      workOrderNumber?: string;
    };

interface OffertePanelState {
  isOpen: boolean;
  offerte: OfferteDetail | null;
  selectedPackageSku: PackageSku;

  phone: string;
  pickupAddress: AddressFields;
  useAlternateDelivery: boolean;
  deliveryAddress: AddressFields;

  petPackageReason: string;

  routes: OfferteRouteOption[];
  selectedRouteId: string | null;
  visibleRoutesCount: number;
  isLoadingRoutes: boolean;
  routesError: string | null;

  // Capacity bump (lead-anchor)
  capacityBumpOpen: boolean;
  capacityBumpRouteId: string | null;
  capacityBumpDelta: number;
  capacityBumpReason: string;
  capacityBumpSubmitting: boolean;
  capacityBumpError: string | null;

  notes: string;

  // Bevestigingen-toggle — uit zetten = S01/S03 uitgesteld tot kantoor activeert vanuit WO-detail.
  bevestigingenSturen: boolean;

  isSubmitting: boolean;
  submitResult: SubmitResult;

  onSuccess: (() => void) | null;

  open: (offerte: OfferteDetail, initialPackageSku: PackageSku, onSuccess?: () => void) => void;
  close: () => void;
  selectPackage: (sku: PackageSku) => void;
  updatePickup: (fields: Partial<AddressFields>) => void;
  toggleAlternateDelivery: () => void;
  updateDelivery: (fields: Partial<AddressFields>) => void;
  setPetReason: (reason: string) => void;
  setPhone: (phone: string) => void;
  loadRoutes: () => Promise<void>;
  selectRoute: (routeId: string | null) => void;
  showMoreRoutes: () => void;
  setNotes: (notes: string) => void;
  setBevestigingenSturen: (v: boolean) => void;
  openCapacityBump: (routeId: string) => void;
  closeCapacityBump: () => void;
  setCapacityBumpDelta: (delta: number) => void;
  setCapacityBumpReason: (reason: string) => void;
  submitCapacityBump: () => Promise<void>;
  submit: () => Promise<void>;
}

const INITIAL_STATE = {
  isOpen: false,
  offerte: null as OfferteDetail | null,
  selectedPackageSku: 'WAS-PACK-VLOERKLEED-BASIS' as PackageSku,

  phone: '',
  pickupAddress: { ...EMPTY_ADDRESS },
  useAlternateDelivery: false,
  deliveryAddress: { ...EMPTY_ADDRESS },

  petPackageReason: '',

  routes: [] as OfferteRouteOption[],
  selectedRouteId: null as string | null,
  visibleRoutesCount: 3,
  isLoadingRoutes: false,
  routesError: null as string | null,

  capacityBumpOpen: false,
  capacityBumpRouteId: null as string | null,
  capacityBumpDelta: 1,
  capacityBumpReason: '',
  capacityBumpSubmitting: false,
  capacityBumpError: null as string | null,

  notes: '',

  bevestigingenSturen: true,

  isSubmitting: false,
  submitResult: null as SubmitResult,

  onSuccess: null as (() => void) | null,
};

export const useOffertePanelStore = create<OffertePanelState>()((set, get) => ({
  ...INITIAL_STATE,

  open: (offerte, initialPackageSku, onSuccess) => {
    set({
      ...INITIAL_STATE,
      isOpen: true,
      offerte,
      selectedPackageSku: initialPackageSku,
      phone: offerte.customer.phone || '',
      pickupAddress: {
        street: offerte.customer.street || '',
        houseNumber: offerte.customer.houseNumber || '',
        houseNumberAddition: '',
        postalCode: offerte.customer.postalCode || '',
        city: offerte.customer.city || '',
      },
      onSuccess: onSuccess ?? null,
    });
    get().loadRoutes();
  },

  close: () => set({ ...INITIAL_STATE }),

  selectPackage: (sku) => set({ selectedPackageSku: sku }),

  updatePickup: (fields) => {
    set((s) => ({ pickupAddress: { ...s.pickupAddress, ...fields } }));
    // Als de lead geen postcode had (alleen Postcodegebied — bv. booking-flow
    // quote-leads), kan de medewerker hier de postcode typen; herlaad dan routes.
    if (fields.postalCode !== undefined) {
      const pc = (fields.postalCode || '').replace(/\s/g, '');
      if (/^\d{4}[A-Za-z]{2}$/.test(pc)) {
        void get().loadRoutes();
      }
    }
  },

  toggleAlternateDelivery: () =>
    set((s) => ({
      useAlternateDelivery: !s.useAlternateDelivery,
      deliveryAddress: !s.useAlternateDelivery ? { ...EMPTY_ADDRESS } : s.deliveryAddress,
    })),

  updateDelivery: (fields) => set((s) => ({ deliveryAddress: { ...s.deliveryAddress, ...fields } })),

  setPetReason: (reason) => set({ petPackageReason: reason }),

  setPhone: (phone) => set({ phone }),

  loadRoutes: async () => {
    const { offerte, pickupAddress } = get();
    if (!offerte) return;
    // Apex-laag verwacht een postcode (zoals "1234AB") en resolved zelf het
    // postcodegebied. Eén canonieke route-lookup voor /orders en /offertes.
    // Voorkeur voor de (bewerkbare) ophaal-postcode in het paneel; valt terug op
    // de lead. Booking-flow quote-leads hebben soms wél een Postcodegebied maar
    // geen PostalCode — dan kan de medewerker de postcode hier invullen.
    const postcode = pickupAddress.postalCode?.trim() || offerte.customer.postalCode;
    if (!postcode) {
      set({ routes: [], routesError: 'Geen postcode bekend — vul het ophaaladres in om routes te laden' });
      return;
    }
    set({ isLoadingRoutes: true, routesError: null });
    try {
      const routes = await fetchOfferteRoutes(postcode);
      set({ routes, isLoadingRoutes: false });
    } catch (err) {
      set({
        isLoadingRoutes: false,
        routesError: err instanceof Error ? err.message : 'Routes laden mislukt',
      });
    }
  },

  selectRoute: (routeId) => set({ selectedRouteId: routeId }),

  showMoreRoutes: () =>
    set((s) => ({
      visibleRoutesCount: Math.min(s.visibleRoutesCount + 3, s.routes.length),
    })),

  setNotes: (notes) => set({ notes }),

  setBevestigingenSturen: (v) => set({ bevestigingenSturen: v }),

  openCapacityBump: (routeId) => set({
    capacityBumpOpen: true,
    capacityBumpRouteId: routeId,
    capacityBumpDelta: 1,
    capacityBumpReason: '',
    capacityBumpError: null,
  }),

  closeCapacityBump: () => set({
    capacityBumpOpen: false,
    capacityBumpRouteId: null,
    capacityBumpDelta: 1,
    capacityBumpReason: '',
    capacityBumpError: null,
  }),

  setCapacityBumpDelta: (capacityBumpDelta) => set({ capacityBumpDelta }),

  setCapacityBumpReason: (capacityBumpReason) => set({ capacityBumpReason }),

  submitCapacityBump: async () => {
    const {
      offerte,
      capacityBumpRouteId,
      capacityBumpDelta,
      capacityBumpReason,
    } = get();
    if (!offerte || !capacityBumpRouteId) return;
    if (capacityBumpReason.trim().length < 3) {
      set({ capacityBumpError: 'Geef een reden op (min. 3 tekens).' });
      return;
    }
    if (capacityBumpDelta < 1 || capacityBumpDelta > 10) {
      set({ capacityBumpError: 'Delta moet tussen 1 en 10 zijn.' });
      return;
    }
    set({ capacityBumpSubmitting: true, capacityBumpError: null });
    try {
      await bumpOfferteRouteCapacity(
        offerte.leadId,
        capacityBumpRouteId,
        'pickup',
        capacityBumpDelta,
        capacityBumpReason.trim()
      );
      set({
        capacityBumpSubmitting: false,
        capacityBumpOpen: false,
        capacityBumpRouteId: null,
        capacityBumpDelta: 1,
        capacityBumpReason: '',
      });
      // Re-fetch zodat de nieuwe capaciteit zichtbaar is in de picker.
      await get().loadRoutes();
    } catch (err) {
      set({
        capacityBumpSubmitting: false,
        capacityBumpError: err instanceof Error ? err.message : 'Capaciteit ophogen mislukt',
      });
    }
  },

  submit: async () => {
    const state = get();
    const { offerte, selectedPackageSku } = state;
    if (!offerte) return;

    if (selectedPackageSku === 'WAS-PACK-VLOERKLEED-PET' && state.petPackageReason.trim().length < 3) {
      set({ submitResult: { ok: false, message: 'Vul een reden in voor het huisdier-pakket' } });
      return;
    }

    set({ isSubmitting: true, submitResult: null });

    try {
      const result = await acceptOfferte(offerte.leadId, {
        packageSku: selectedPackageSku,
        phone: state.phone.trim() || undefined,
        pickupStreet: state.pickupAddress.street || undefined,
        pickupHouseNumber: state.pickupAddress.houseNumber || undefined,
        pickupHouseNumberAddition: state.pickupAddress.houseNumberAddition || undefined,
        pickupPostalCode: state.pickupAddress.postalCode || undefined,
        pickupCity: state.pickupAddress.city || undefined,
        useAlternateDelivery: state.useAlternateDelivery,
        deliveryStreet: state.useAlternateDelivery ? state.deliveryAddress.street : undefined,
        deliveryHouseNumber: state.useAlternateDelivery ? state.deliveryAddress.houseNumber : undefined,
        deliveryHouseNumberAddition: state.useAlternateDelivery ? state.deliveryAddress.houseNumberAddition : undefined,
        deliveryPostalCode: state.useAlternateDelivery ? state.deliveryAddress.postalCode : undefined,
        deliveryCity: state.useAlternateDelivery ? state.deliveryAddress.city : undefined,
        petPackageReason:
          selectedPackageSku === 'WAS-PACK-VLOERKLEED-PET' ? state.petPackageReason.trim() : undefined,
        selectedRouteId: state.selectedRouteId,
        notes: state.notes.trim() || undefined,
        bevestigingenSturen: state.bevestigingenSturen,
      });

      set({
        isSubmitting: false,
        submitResult: {
          ok: true,
          message: result.message || 'Offerte geaccepteerd',
          workOrderId: result.workOrderId,
          workOrderNumber: result.workOrderNumber,
        },
      });
      state.onSuccess?.();
    } catch (err) {
      set({
        isSubmitting: false,
        submitResult: {
          ok: false,
          message: err instanceof Error ? err.message : 'Accepteren mislukt',
        },
      });
    }
  },
}));
